/**
 * Regles de l'ajust d'existències per Lot + Lloc (OBR-143) — funcions PURES, el mirall del que fa complir la base de dades.
 *
 * LA POLÍTICA VIU EN UN SOL LLOC, I AQUÍ N'HI HA EL MIRALL
 * El servidor decideix (RPC `adjust_stock` i `create_adjustment_lot`, el trigger `stock_without_lot` i la funció
 * `lot_requires_expiry`); això és el que la pantalla fa servir per dir-ho ABANS d'enviar res. El servidor continua
 * sent l'autoritat: el saldo pot haver canviat mentre el diàleg era obert. Per això els codis d'error d'aquí són EXACTAMENT
 * els que la base de dades posa al missatge (`RAISE EXCEPTION '<codi>'`), i `stockErrorCode` els llegeix d'un missatge
 * d'error de l'API.
 *
 * Decisions (docs/Pla_Implementacio_Existencies.md §1):
 *  · D1 — un article demana caducitat als lots quan té existències gestionades i no és un auxiliar (envàs).
 *  · D2 — un ajust mai deixa un saldo per sota de zero (el consum d'un operari sense connexió sí que pot: s'ha de poder
 *    arreglar després, a «A regularitzar»).
 *  · D3 — afegir estoc a un article d'existències gestionades demana lot. L'única sortida és saldar un NEGATIU d'estoc sense
 *    lot fins a zero: és arreglar un sobre-consum, no fer entrar res de nou.
 *
 * Pur: sense I/O, sense rellotge i sense atzar.
 */

import type { ArticleType, MovementReason } from '../enums/index.js';
import { STOCK_QUANTITY_EPSILON } from './stockImport.js';

/**
 * Els motius d'un ajust manual (subconjunt de `movement_reason`: els altres —recepció, producció, consum— són dels fluxos que
 * escriuen els seus propis moviments). El motiu ESCRIT és obligatori a part.
 */
export const STOCK_ADJUST_REASONS = [
  'recount',
  'loss',
  'damage',
  'expired',
  'burnt',
  'dropped',
  'correction',
] as const satisfies readonly MovementReason[];
export type StockAdjustReason = (typeof STOCK_ADJUST_REASONS)[number];

export function isStockAdjustReason(value: string): value is StockAdjustReason {
  return (STOCK_ADJUST_REASONS as readonly string[]).includes(value);
}

/**
 * D1 — l'article demana caducitat als seus lots. Mirall de `lot_requires_expiry(article_id)`: existències gestionades i
 * tipologia diferent d'auxiliar (`article_type` 'packaging': envasos, etiquetes…).
 */
export function lotRequiresExpiry(article: {
  readonly is_stock_tracked: boolean;
  readonly article_type: ArticleType;
}): boolean {
  return article.is_stock_tracked && article.article_type !== 'packaging';
}

/**
 * D3 — l'article pot rebre un LOT NOU d'ajust (un lot de proveïdor). Mirall de `create_adjustment_lot`: els articles que es
 * compren (matèries primeres, auxiliars i productes revenuts) en tenen; un producte només de producció, no: el seu lot el crea
 * una ordre de treball amb la seva versió de recepta (`lot_requires_work_order`).
 */
export function canCreateAdjustmentLot(article: {
  readonly article_type: ArticleType;
  readonly is_purchased: boolean;
}): boolean {
  return (
    article.article_type === 'raw_material' ||
    article.article_type === 'packaging' ||
    article.is_purchased
  );
}

// ── Errors d'aplicació ────────────────────────────────────────────────────────────────

/** Els errors d'`adjust_stock` i `create_adjustment_lot` (el codi va al `message`). */
export const STOCK_ADJUST_ERROR_CODES = [
  'adjust_stock_forbidden',
  'adjust_stock_bad_quantity',
  'adjust_stock_bad_reason',
  'adjust_stock_note_required',
  'adjust_stock_two_lots',
  'adjust_stock_unknown_article',
  'adjust_stock_untracked',
  'adjust_stock_unknown_lot',
  'adjust_stock_bad_place',
  'adjust_stock_lot_required',
  'adjust_stock_expiry_required',
  'adjust_stock_negative',
  'adjust_stock_client_uuid_conflict',
  'lot_requires_work_order',
] as const;
export type StockAdjustErrorCode = (typeof STOCK_ADJUST_ERROR_CODES)[number];

/** Els errors de `set_stock_state`. */
export const STOCK_STATE_ERROR_CODES = [
  'set_stock_state_forbidden',
  'set_stock_state_bad_state',
  'set_stock_state_lot_required',
  'set_stock_state_unknown_lot',
  'set_stock_state_bad_place',
] as const;
export type StockStateErrorCode = (typeof STOCK_STATE_ERROR_CODES)[number];

/** El trigger de `stock_movements` que rebutja un ajust positiu sense lot (quan algú escriu directament a la taula). */
export const STOCK_TRIGGER_ERROR_CODES = ['stock_without_lot'] as const;

export type StockErrorCode =
  | StockAdjustErrorCode
  | StockStateErrorCode
  | (typeof STOCK_TRIGGER_ERROR_CODES)[number];

const ALL_STOCK_ERROR_CODES: readonly StockErrorCode[] = [
  ...STOCK_ADJUST_ERROR_CODES,
  ...STOCK_STATE_ERROR_CODES,
  ...STOCK_TRIGGER_ERROR_CODES,
];

/** El codi d'aplicació que porta un missatge d'error (l'SDK hi pot afegir context), o null si no n'hi ha cap de conegut. */
export function stockErrorCode(message: string | null | undefined): StockErrorCode | null {
  if (!message) return null;
  return ALL_STOCK_ERROR_CODES.find((code) => message.includes(code)) ?? null;
}

/**
 * La clau i18n de la frase d'obrador de cada error: `backoffice.stockLine` (els que la pantalla ja explica al seu lloc) i
 * `backoffice.stockErrors` (els que no pot evitar amb la seva validació). Cap error no es mostra mai cru: un codi que no es
 * coneix surt amb la frase genèrica `STOCK_ERROR_GENERIC_KEY`.
 */
export const STOCK_ERROR_I18N_KEYS: Readonly<Record<StockErrorCode, string>> = {
  adjust_stock_forbidden: 'backoffice.stockLine:errForbidden',
  adjust_stock_bad_quantity: 'backoffice.stockErrors:badQuantity',
  adjust_stock_bad_reason: 'backoffice.stockErrors:badReason',
  adjust_stock_note_required: 'backoffice.stockErrors:noteRequired',
  adjust_stock_two_lots: 'backoffice.stockErrors:twoLots',
  adjust_stock_unknown_article: 'backoffice.stockErrors:unknownArticle',
  adjust_stock_untracked: 'backoffice.stockErrors:untracked',
  adjust_stock_unknown_lot: 'backoffice.stockErrors:unknownLot',
  adjust_stock_bad_place: 'backoffice.stockLine:errBadPlace',
  adjust_stock_lot_required: 'backoffice.stockLine:errLotRequired',
  adjust_stock_expiry_required: 'backoffice.stockLine:errExpiryRequired',
  adjust_stock_negative: 'backoffice.stockLine:errNegative',
  adjust_stock_client_uuid_conflict: 'backoffice.stockErrors:clientUuidConflict',
  lot_requires_work_order: 'backoffice.stockLine:errWorkOrder',
  set_stock_state_forbidden: 'backoffice.stockLine:errStateForbidden',
  set_stock_state_bad_state: 'backoffice.stockErrors:stateBadState',
  set_stock_state_lot_required: 'backoffice.stockErrors:stateLotRequired',
  set_stock_state_unknown_lot: 'backoffice.stockErrors:unknownLot',
  set_stock_state_bad_place: 'backoffice.stockLine:errBadPlace',
  stock_without_lot: 'backoffice.stockLine:errLotRequired',
};

export const STOCK_ERROR_GENERIC_KEY = 'backoffice.stockLine:errGeneric';

/** La clau i18n del missatge d'un error de les RPC d'Existències (la frase genèrica quan no se'l coneix). */
export function stockErrorKey(error: unknown): string {
  const message =
    typeof error === 'string' ? error : error instanceof Error ? error.message : undefined;
  const code = stockErrorCode(message);
  return code === null ? STOCK_ERROR_GENERIC_KEY : STOCK_ERROR_I18N_KEYS[code];
}

// ── Validació d'un ajust, abans d'enviar-lo ────────────────────────────────────────────

/**
 * D3 — l'excepció a «afegir demana lot»: saldar un NEGATIU d'estoc sense lot. Només quan el saldo de la línia és negatiu i
 * l'ajust no el deixa per damunt de zero. Mirall exacte de la condició de `adjust_stock` i del trigger.
 */
export function canSettleUnlottedBalance(balance: number, quantity: number): boolean {
  return balance < -STOCK_QUANTITY_EPSILON && balance + quantity <= STOCK_QUANTITY_EPSILON;
}

/** El que hi quedarà a la línia si l'ajust es desa (el saldo més la variació, sense soroll de coma flotant). */
export function balanceAfterAdjustment(balance: number, quantity: number): number {
  return Math.round((balance + quantity) * 1000) / 1000;
}

/** Què sap la pantalla d'un ajust abans d'enviar-lo. Les quantitats, en UNITAT BASE de l'article. */
export interface AdjustmentFacts {
  /** La variació amb signe: positiva afegeix, negativa treu. */
  readonly quantity: number;
  /** El motiu triat (un de {@link STOCK_ADJUST_REASONS}). */
  readonly reason: string | null;
  /** El motiu escrit. */
  readonly note: string | null;
  readonly hasSupplierLot: boolean;
  readonly hasFinishedLot: boolean;
  /** El saldo de la línia (lot + lloc) ara. */
  readonly balance: number;
  /** L'article demana caducitat als lots ({@link lotRequiresExpiry}). */
  readonly requiresExpiry: boolean;
  /** La caducitat que el lot ja té, o null. */
  readonly lotExpiryDate: string | null;
  /** La caducitat que s'ha escrit al formulari, o null. */
  readonly expiryDate: string | null;
}

/** Els errors que una validació local pot predir (els que el servidor posaria en el mateix ordre). */
export type AdjustmentIssue = Extract<
  StockAdjustErrorCode,
  | 'adjust_stock_bad_quantity'
  | 'adjust_stock_bad_reason'
  | 'adjust_stock_note_required'
  | 'adjust_stock_two_lots'
  | 'adjust_stock_lot_required'
  | 'adjust_stock_expiry_required'
  | 'adjust_stock_negative'
>;

/**
 * Valida un ajust amb les mateixes regles que `adjust_stock`, i en el mateix ordre: el primer que torna és el que el
 * servidor respondria. Una llista buida vol dir que s'hi pot enviar (el servidor continua tenint l'última paraula).
 */
export function validateAdjustment(facts: AdjustmentFacts): readonly AdjustmentIssue[] {
  const issues: AdjustmentIssue[] = [];
  const hasLot = facts.hasSupplierLot || facts.hasFinishedLot;

  if (!Number.isFinite(facts.quantity) || facts.quantity === 0) issues.push('adjust_stock_bad_quantity');
  if (facts.reason === null || !isStockAdjustReason(facts.reason)) issues.push('adjust_stock_bad_reason');
  if (facts.note === null || facts.note.trim() === '') issues.push('adjust_stock_note_required');
  if (facts.hasSupplierLot && facts.hasFinishedLot) issues.push('adjust_stock_two_lots');

  if (Number.isFinite(facts.quantity) && facts.quantity > 0) {
    if (!hasLot && !canSettleUnlottedBalance(facts.balance, facts.quantity)) {
      issues.push('adjust_stock_lot_required');
    }
    if (hasLot && facts.requiresExpiry && facts.lotExpiryDate === null && facts.expiryDate === null) {
      issues.push('adjust_stock_expiry_required');
    }
  } else if (
    Number.isFinite(facts.quantity) &&
    facts.quantity < 0 &&
    facts.balance + facts.quantity < -STOCK_QUANTITY_EPSILON
  ) {
    issues.push('adjust_stock_negative');
  }
  return issues;
}

/** Què sap la pantalla d'un lot nou d'ajust abans d'enviar-lo. */
export interface AdjustmentLotFacts {
  /** La quantitat del lot nou, en unitat base (ha de ser més gran que zero). */
  readonly quantity: number;
  readonly reason: string | null;
  readonly note: string | null;
  /** L'article demana caducitat als lots. */
  readonly requiresExpiry: boolean;
  /** La caducitat escrita, o null. */
  readonly expiryDate: string | null;
  /** L'article admet lots de proveïdor ({@link canCreateAdjustmentLot}). */
  readonly canCreateLot: boolean;
}

export type AdjustmentLotIssue = Extract<
  StockAdjustErrorCode,
  | 'adjust_stock_bad_quantity'
  | 'adjust_stock_bad_reason'
  | 'adjust_stock_note_required'
  | 'lot_requires_work_order'
  | 'adjust_stock_expiry_required'
>;

/** Valida un lot nou d'ajust amb les mateixes regles (i ordre) que `create_adjustment_lot`. */
export function validateAdjustmentLot(facts: AdjustmentLotFacts): readonly AdjustmentLotIssue[] {
  const issues: AdjustmentLotIssue[] = [];
  if (!Number.isFinite(facts.quantity) || facts.quantity <= 0) issues.push('adjust_stock_bad_quantity');
  if (facts.reason === null || !isStockAdjustReason(facts.reason)) issues.push('adjust_stock_bad_reason');
  if (facts.note === null || facts.note.trim() === '') issues.push('adjust_stock_note_required');
  if (!facts.canCreateLot) issues.push('lot_requires_work_order');
  if (facts.requiresExpiry && facts.expiryDate === null) issues.push('adjust_stock_expiry_required');
  return issues;
}
